















import { pointer, select, pie as d3Pie, arc as d3Arc, PieArcDatum } from 'd3'
import { Guid } from 'guid-typescript'
import { sum } from 'lodash'
import Vue from 'vue'
import { Component, Prop } from 'vue-property-decorator'

import { RandomColor } from "../logic/helper";
import { PieChartDataRecord, PieChartOptions } from './types'

type PieChartRecordPercent = {
  percent: number
} & PieChartDataRecord

@Component
export default class PieChart extends Vue {
  guid = Guid.create().toString()

  @Prop({ type: Array, default: () => [], required: true }) data!:Array<PieChartDataRecord>

  @Prop({ type: Object, required: true }) options!:PieChartOptions

  get preparedData() {
    return this.toPercents(this.data)
  }

  get pieStyles(): Record<string, number> {
    return {
      height: this.options.chart.height ?? 200,
      width: this.options.chart.width ?? 200
    }
  }

  get valuesSum():number {
    return sum(this.data.map(l => l.data))
  }

  get calculateRadius():number {
    const { width, height } = this.options.chart

    return Math.min(width, height) / 2
  }

  get getInnerRadius():number {
    return this.options.chart.innerRadius ?? 0
  }

  setChartSize(): void {
    select(`#container-${ this.guid }`)
      .attr('height', this.pieStyles.height)
      .attr('width', this.pieStyles.width)
  }

  setColors(_: PieArcDatum<PieChartRecordPercent>, index:number):string {
    if (this.options.colors && this.options.colors[index]) {
      return this.options.colors[index]
    }

    return RandomColor()
  }

  toPercents(list: Array<PieChartDataRecord>):Array<PieChartRecordPercent> {
    return list.slice().map(value => ({
      percent: Number.parseInt(((value.data / this.valuesSum) * 100).toFixed(2)),
      ...value
    }))
  }

  handleMouseEnter(evt: MouseEvent, d: PieArcDatum<PieChartRecordPercent>): void {
    const [ xMouse, yMouse ] = pointer(evt)
    const { name, data } = d.data

    const dataPercent = this.preparedData.find((d) => d.name === name)!.percent

    select(`#tooltip-${ this.guid }`)
      .style('opacity', 1)
      .style('left', `${ 10 + xMouse + this.pieStyles.width / 2 }px`)
      .style('top', `${ 10 + yMouse + this.pieStyles.height / 2  }px`)
      .text(`${ name }: ${ this.options.chart.legendDataPercent ? Math.floor(Number(dataPercent)) + '%' : data }`)
  }

  handleMousemove(evt: MouseEvent, _:PieArcDatum<PieChartRecordPercent>):void {
    const [ xMouse, yMouse ] = pointer(evt)

    select(`#tooltip-${ this.guid }`)
      .style('left', `${ 10 + xMouse + this.pieStyles.width / 2 }px`)
      .style('top', `${ 10 + yMouse + this.pieStyles.height / 2 }px`)
  }

  handleMouseout(evt: MouseEvent, _:PieArcDatum<PieChartRecordPercent>):void {
    select(`#tooltip-${ this.guid }`)
      .style('opacity', 0)
  }

  mounted(): void {
    this.setChartSize()
    const { width, height } = this.options.chart

    const
      svg = select(`#container-${ this.guid }`),
      g = svg.append("g").attr("transform", "translate(" + width / 2 + "," + height / 2 + ")");

    // Generate the pie
    const pie = d3Pie<PieChartRecordPercent>()
      .value((d) => d.percent)

    // Generate the arcs
    const arc = d3Arc<PieArcDatum<PieChartRecordPercent>>()
      .outerRadius(this.calculateRadius)
      .innerRadius(this.getInnerRadius)

    //Generate groups
    const arcs = g.selectAll("arc")
      .data(pie(this.preparedData))
      .enter()
      .append("g")
      .attr("class", "arc")
      .on('mouseenter', this.handleMouseEnter)
      .on('mousemove', this.handleMousemove)
      .on('mouseout', this.handleMouseout)

    //Draw arc paths
    arcs.append("path")
      .attr("fill", this.setColors)
      .attr("d", arc);

    if (this.options.chart.showLabels) {
      g.selectAll("arc")
        .data(pie(this.preparedData))
        .enter()
        .append('text')
        .text((d) => `${ d.data.percent }%`)
        .attr("transform", (d) => 'translate(' + arc.centroid(d) + ')')
        .style("text-anchor", "middle")
        .style("font", this.options.labels && this.options.labels.font ? this.options.labels.font : 'bold 14px sans-serif')
        .style("fill", this.options.labels && this.options.labels.fill ? this.options.labels.fill : '#fff')
    }
  }
}
